import { cn } from "@/lib/utils";

/**
 * Official GuestMaker artwork. The <path>/<rect> elements below (the M mark,
 * the ten wordmark glyph paths, and the fuchsia heart group) are copied
 * verbatim from the official source files in the sibling `guestmaker` repo:
 *   - Logotype: /Users/a1234/Developer/guestmaker/public/logo-full.svg
 *   - Mark:     /Users/a1234/Developer/guestmaker/public/logo-icon.svg
 * Do not edit any `d`/`transform`/`x`/`y`/`width`/`height` value by hand —
 * regenerate from those source files instead.
 *
 * The `viewBox` on each variant is tightened from the source files' full
 * canvas to just the artwork's bounding box (e.g. "280 220 640 745" for the
 * mark, not "0 0 <canvas size>"). The path/rect coordinates are untouched;
 * only the viewBox origin/size changed, to crop out the surrounding empty
 * canvas so the rendered SVG has no invisible padding. The width math below
 * (`(height * viewBoxWidth) / viewBoxHeight`) derives from that tightened
 * viewBox, so it must be recomputed together if the viewBox ever changes.
 */

export interface GuestmakerLogoProps {
  variant?: "full" | "mark";
  /** light = white M/wordmark for dark backgrounds; dark = ink for light backgrounds */
  tone?: "light" | "dark";
  /** Rendered height in px; width follows proportionally. */
  height?: number;
  className?: string;
}

export function GuestmakerLogo({
  variant = "full",
  tone = "light",
  height = 28,
  className,
}: GuestmakerLogoProps) {
  const color = tone === "light" ? "#FFFFFF" : "#0A0A0A";

  if (variant === "mark") {
    const width = (height * 640) / 745;
    return (
      <svg
        role="img"
        aria-label="GuestMaker"
        className={cn("gm-logo", className)}
        width={width}
        height={height}
        viewBox="280 220 640 745"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
        style={{ color, display: "block" }}
      >
        <path d="M901.98,946.76h-120.79v-273.54l-173.69,168.11h-14.11l-173.69-167.23v272.66h-121.67v-455.68h125.2l177.22,173.4,176.34-173.4h125.2v455.68Z" />
        <g>
          <path
            d="M560.53,236.9h0c45.87,0,83.12,37.24,83.12,83.12v194.64h-166.23v-194.64c0-45.87,37.24-83.12,83.12-83.12Z"
            transform="translate(-101.54 506.42) rotate(-45)"
            fill="#ec71fe"
            opacity=".5"
          />
          <path
            d="M717.51,415.21l20.16-20.09c32.46-32.46,32.46-85.09,0-117.55h0c-32.46-32.46-85.09-32.46-117.55,0l-20.12,20.12"
            fill="#ec71fe"
          />
          <rect
            x="516.93"
            y="332.12"
            width="166.18"
            height="166.23"
            transform="translate(-117.87 545.89) rotate(-45)"
            fill="#ec71fe"
            opacity=".6"
          />
        </g>
      </svg>
    );
  }

  const width = (height * 1500) / 1085;

  return (
    <svg
      role="img"
      aria-label="GuestMaker"
      className={cn("gm-logo", className)}
      width={width}
      height={height}
      viewBox="200 185 1500 1085"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      style={{ color, display: "block" }}
    >
      <g>
        <path d="M1254.16,846.39h-125.51v-177.07l-180.49,174.68h-14.66l-180.49-173.77v176.16h-126.43v-366.33l.93-129.17,313.32,309.35,312.25-309.19,1.08,129.01v366.33Z" />
        <g>
          <path
            d="M899.36,215.94h0c47.67,0,86.37,38.7,86.37,86.37v202.25h-172.73v-202.25c0-47.67,38.7-86.37,86.37-86.37Z"
            transform="translate(8.68 741.46) rotate(-45)"
            fill="#ec71fe"
            opacity=".5"
          />
          <path
            d="M1062.48,401.22l20.95-20.87c33.73-33.73,33.73-88.41,0-122.14h0c-33.73-33.73-88.41-33.73-122.14,0l-20.91,20.91"
            fill="#ec71fe"
          />
          <rect
            x="854.05"
            y="314.87"
            width="172.68"
            height="172.73"
            transform="translate(-8.29 782.47) rotate(-45)"
            fill="#ec71fe"
            opacity=".6"
          />
        </g>
      </g>
      <g>
        <path d="M402.63,1171.28c0,53.33-37.75,88.7-90.81,88.7-57.81,0-99-43.03-99-95.83s41.71-96.35,96.89-96.35c35.11,0,64.94,17.69,80.52,43.03l-31.15,17.95c-8.45-14.78-27.19-25.61-49.63-25.61-35.11,0-60.45,25.87-60.45,60.98s24.55,60.19,63.09,60.19c29.04,0,47.52-13.99,53.85-35.11h-55.96v-32.21h92.66v14.25Z" />
        <path d="M549.38,1124.29v131.99h-34.06v-14.78c-7.92,11.88-21.65,18.48-39.33,18.48-27.98,0-49.89-19.53-49.89-54.64v-81.04h34.05v76.82c0,17.95,10.82,27.19,26.13,27.19,16.63,0,29.04-9.77,29.04-32.74v-71.28h34.06Z" />
        <path d="M646.51,1229.09c13.2,0,23.76-5.54,29.57-13.2l27.46,15.84c-12.41,17.95-32.21,28.25-57.55,28.25-44.35,0-72.33-30.36-72.33-69.69s28.25-69.69,69.69-69.69c39.07,0,66.79,30.89,66.79,69.69,0,5.01-.52,9.5-1.32,13.99h-99.52c4.75,17.42,19.27,24.81,37.22,24.81ZM676.08,1177.88c-4.22-19.01-18.48-26.66-32.74-26.66-18.21,0-30.62,9.77-34.58,26.66h67.32Z" />
        <path d="M828.13,1217.74c0,28.51-24.81,42.24-53.06,42.24-26.4,0-45.93-11.09-55.44-31.41l29.57-16.63c3.7,10.82,12.67,17.16,25.87,17.16,10.83,0,18.21-3.7,18.21-11.35,0-19.27-68.1-8.71-68.1-55.17,0-26.93,22.97-41.97,50.15-41.97,21.39,0,39.86,9.77,50.16,27.98l-29.04,15.84c-3.96-8.45-11.35-13.46-21.12-13.46-8.44,0-15.31,3.7-15.31,10.82,0,19.54,68.1,7.39,68.1,55.97Z" />
        <path d="M891.74,1157.03v54.91c0,14.26,10.3,14.52,29.83,13.46v30.89c-47.78,5.28-63.88-8.71-63.88-44.35v-54.91h-22.97v-32.74h22.97v-26.66l34.05-10.3v36.96h29.83v32.74h-29.83Z" />
        <path d="M1130.11,1256.28h-36.16v-119.85l-52.01,85.8h-4.23l-52-85.53v119.59h-36.43v-184.79h37.49l53.06,87.38,52.8-87.38h37.48v184.79Z" />
        <path d="M1297.19,1124.29v131.99h-34.05v-15.57c-9.51,11.88-23.76,19.27-43.03,19.27-35.11,0-64.15-30.36-64.15-69.69s29.04-69.69,64.15-69.69c19.27,0,33.52,7.39,43.03,19.27v-15.58h34.05ZM1263.14,1190.29c0-22.18-15.58-37.22-36.7-37.22s-36.43,15.05-36.43,37.22,15.58,37.22,36.43,37.22,36.7-15.05,36.7-37.22Z" />
        <path d="M1410.16,1256.28l-48.05-59.93v59.93h-34.06v-184.79h34.06v110.87l45.4-58.08h40.66l-53.06,65.21,54.64,66.79h-39.6Z" />
        <path d="M1523.14,1229.09c13.2,0,23.76-5.54,29.56-13.2l27.45,15.84c-12.4,17.95-32.2,28.25-57.55,28.25-44.35,0-72.33-30.36-72.33-69.69s28.25-69.69,69.69-69.69c39.07,0,66.79,30.89,66.79,69.69,0,5.01-.53,9.5-1.32,13.99h-99.52c4.75,17.42,19.27,24.81,37.23,24.81ZM1552.7,1177.88c-4.23-19.01-18.48-26.66-32.73-26.66-18.22,0-30.62,9.77-34.58,26.66h67.31Z" />
        <path d="M1684.68,1121.65v38.01c-17.69-2.11-40.12,5.81-40.12,33.53v63.09h-34.06v-131.99h34.06v22.7c6.86-17.95,23.49-25.34,40.12-25.34Z" />
      </g>
    </svg>
  );
}
